<template>
  <div class="loader">
    <div class="stack" role="status" aria-live="polite">
      <div
        v-for="(item, index) in visibleMessages"
        :key="item.id"
        class="card"
        :class="{ current: index === visibleMessages.length - 1 }"
        :style="cardStyle(item, index)"
      >
        <span
          v-if="showSpinner && index === visibleMessages.length - 1"
          class="spinner"
          aria-hidden="true"
        >
          <i></i><i></i><i></i>
        </span>
        <span class="text">{{ item.text }}</span>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * StackLoader
 * -----------
 * A "filing cabinet" style progressive loading indicator. Each call to
 * `addMessage()` pushes a new toast-style card to the front of the stack;
 * previous cards animate back, up, and fade behind it so the user can see
 * the progression of loading states.
 *
 * Usage from a parent:
 *
 *   <StackLoader ref="loader" />
 *
 *   this.$refs.loader.addMessage('Connecting...')
 *   this.$refs.loader.addMessage('Fetching data...')
 *   this.$refs.loader.addMessage('Almost done...')
 *   this.$refs.loader.reset() // clear the stack manually if needed
 */
export default {
  name: 'StackLoader',
  components: {},
  props: {
    // how many cards are visible in the stack at once (older ones drop off)
    maxVisible: { type: Number, default: 5 },
    // how many messages to retain internally (memory cap)
    maxHistory: { type: Number, default: 20 },
    // vertical distance (px) each card sits behind/above the one in front
    stackOffset: { type: Number, default: 14 },
    // how much smaller each card behind gets, per depth level
    scaleStep: { type: Number, default: 0.055 },
    // how much more transparent each card behind gets, per depth level
    fadeStep: { type: Number, default: 0.2 },
    // opacity floor so far-back cards never fully disappear
    minOpacity: { type: Number, default: 0.12 },
    // show the animated dots on the front-most card
    showSpinner: { type: Boolean, default: true }
  },
  data() {
    return {
      messages: [],
      uid: 0
    };
  },
  computed: {
    visibleMessages() {
      if (this.messages.length <= this.maxVisible) return this.messages;
      return this.messages.slice(this.messages.length - this.maxVisible);
    }
  },
  methods: {
    // public: call this from the parent with the current loading message
    addMessage(text) {
      if (!text) return;

      const item = {
        id: ++this.uid,
        text,
        entering: true
      };
      this.messages.push(item);

      if (this.messages.length > this.maxHistory) {
        this.messages.splice(0, this.messages.length - this.maxHistory);
      }

      // let it mount in its "just arrived" state first, then release it
      // into its resting position so the transition actually animates
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            item.entering = false;
          });
        });
      });
    },
    // public: wipe the stack back to empty
    reset() {
      this.messages = [];
      this.uid = 0;
    },
    cardStyle(item, index) {
      if (item.entering) {
        return {
          '--offset-y': '26px',
          '--scale': '0.94',
          '--opacity': '0',
          '--blur': '0px',
          zIndex: index
        };
      }

      const total = this.visibleMessages.length;
      const depth = total - 1 - index; // 0 = current / front card

      return {
        '--offset-y': `${-(depth * this.stackOffset)}px`,
        '--scale': `${Math.max(1 - depth * this.scaleStep, 0.5)}`,
        '--opacity': `${Math.max(1 - depth * this.fadeStep, this.minOpacity)}`,
        '--blur': `${Math.min(depth * 0.6, 2)}px`,
        zIndex: index
      };
    }
  },
  // reset on unmount (covers both Vue 2 and Vue 3 lifecycle names)
  beforeDestroy() {
    this.reset();
  },
  destroyed() {
    this.reset();
  },
  beforeUnmount() {
    this.reset();
  },
  unmounted() {
    this.reset();
  }
};
</script>

<style lang="scss" scoped>
.loader {
  position: fixed;
  top: 80px;
  left: calc(50% - 320px/2);
  display: inline-block;
  width: 100%;
  max-width: 320px;
  z-index: 9;
}

.stack {
  position: relative;
  height: 60px;
}

.card {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 18px;
  border-radius: 12px;
  background: black;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow:
    0 1px 2px rgba(0, 0, 0, 0.4),
    0 8px 24px -4px rgba(0, 0, 0, 0.6);

  transform-origin: center bottom;
  transform: translateY(var(--offset-y, 0)) scale(var(--scale, 1));
  opacity: var(--opacity, 1);
  filter: blur(var(--blur, 0px));

  transition:
    transform 0.45s cubic-bezier(0.22, 1, 0.36, 1),
    opacity 0.45s ease,
    filter 0.45s ease,
    box-shadow 0.45s ease;
  will-change: transform, opacity;
  pointer-events: none;

  &.current {
    pointer-events: auto;
    box-shadow:
      0 2px 4px rgba(0, 0, 0, 0.5),
      0 16px 40px -8px rgba(0, 0, 0, 0.75);
  }
}

.text {
  font-size: 14px;
  font-weight: 500;
  color: #f1f1f4;
  //margin-left: 20px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
  text-align:center;
  margin: 0 auto;
}

.spinner {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;

  i {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #f1f1f4;
    animation: bounce 1s ease-in-out infinite;
    font-style: normal;

    &:nth-child(1) { animation-delay: 0s; }
    &:nth-child(2) { animation-delay: 0.15s; }
    &:nth-child(3) { animation-delay: 0.3s; }
  }
}

@keyframes bounce {
  0%, 80%, 100% { transform: scale(0.6); opacity: 0.5; }
  40% { transform: scale(1); opacity: 1; }
}
</style>
